import type { ComponentChildren, h } from "preact";
import { useRef, useState } from "preact/hooks";
import type { Member, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { type Suggestion, useSuggestions } from "../../lib/commentSuggestions.ts";
import { lookup, opened, parts, token } from "../../lib/commentText.ts";
import type { WrittenAbout } from "../../lib/conversation.ts";
import { rememberEmoji } from "../../lib/emoji.ts";
import { entityOf } from "../../lib/entities.ts";
import { useItemPicks } from "../../lib/itemPicks.ts";
import { type ItemPick, outsideOf, spaceUrl } from "../../lib/paths.ts";
import { peek } from "../../lib/peek.ts";
import { useSpacePeople } from "../../lib/people.ts";
import { isPlainClick } from "../../lib/router.ts";
import { useId } from "../../lib/uid.ts";
import { Link } from "../../ui/Button.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { Textarea } from "../../ui/form.tsx";
import { hues, ICON_TEXT, ICON_WASH } from "../../ui/iconColors.ts";
import { NAV_ICONS } from "../../ui/icons.tsx";
import { InlineKindIcon } from "../items/icons.tsx";
import { Suggestions } from "./suggestions.tsx";

const SPACE = /\s/;

/**
 * An icon's room in a line of text: 14px square, a little under the baseline, empty or not (the line is as tall
 * either way: nothing moves when the icon comes).
 */
const SLOT = "inline-block size-3.5 align-[-0.15em] overflow-hidden";

/** A mention inside a text: a small chip, so it reads as a reference, not as the subject. */
const CHIP = "rounded px-1 py-px text-[0.92em] font-medium whitespace-nowrap";

/**
 * A link to an item inside a text, flowing with it (it wraps like words): its kind's (or file's) icon, then its title
 * and its entity's icon (named in its tooltip) on the entity's tint. Until the item's known, the icons for "an item"
 * and "no entity" hold their room (nothing moves when the real ones fade in; only an item without an entity gives
 * its back); `#` for one the reader can't see.
 */
function ItemChip({
  space,
  id,
  label,
  pick,
}: {
  space: SpaceOut;
  id: string;
  label: string;
  pick: ItemPick | null | undefined;
}) {
  const entity = entityOf(space, pick?.entity);
  const title = label || t("common.untitled");
  // Its title (and entity's icon) on its entity's tint, lighter, in its color; the kind's icon before, on the text's
  // ground.
  const hue = entity?.icon ? hues(entity.icon) : null;
  // Waited for (not known when it came): what it waited for arrives in turn (its `--stagger`), over the placeholders.
  const waited = useRef(pick === undefined).current;
  const arrive = waited ? "block motion-safe:animate-fade-in" : "block";
  const leaving = (placeholder: ComponentChildren) =>
    waited ? (
      <span class="absolute inset-0 motion-safe:animate-fade-out motion-reduce:hidden">{placeholder}</span>
    ) : null;
  const itemIcon = <NAV_ICONS.items size={14} class="block text-zinc-300" />;
  const noEntity = <EntityGlyph icon={null} size={14} empty="none" />;
  return (
    <Link
      href={spaceUrl(space, `/items/${id}`)}
      title={title}
      data-chip={id}
      data-pending={pick === undefined ? "" : undefined}
      onClick={(e) => {
        // A quick look beside the text (its page is a modified click away), for one the reader sees, in its space.
        if (isPlainClick(e) && pick !== null && !outsideOf(space)) {
          e.preventDefault();
          peek(id, e.currentTarget);
        }
      }}
      class="group/chip text-[0.92em] font-medium text-zinc-800"
    >
      <span class={`relative mr-0.5 ${SLOT} text-center leading-3.5 text-zinc-400`}>
        {pick ? (
          <>
            {leaving(itemIcon)}
            <span class={arrive}>
              <InlineKindIcon item={pick} />
            </span>
          </>
        ) : pick === null ? (
          "#"
        ) : (
          itemIcon
        )}
      </span>
      <span
        class={`rounded px-1 py-px box-decoration-clone transition-colors delay-(--stagger) duration-500 ${ICON_WASH[hue?.tint ?? "gray"]} ${hue ? ICON_TEXT[hue.color] : ""}`}
      >
        {title}
        {entity || pick === undefined ? (
          <span class={`relative ml-1 ${SLOT}`} title={entity?.name}>
            {entity ? (
              <>
                {leaving(noEntity)}
                <span class={arrive}>
                  <EntityGlyph
                    icon={entity.icon && hue ? { name: entity.icon.name, color: hue.color } : null}
                    size={14}
                    empty="dot"
                  />
                </span>
              </>
            ) : (
              noEntity
            )}
          </span>
        ) : null}
      </span>
    </Link>
  );
}

/**
 * Text people and agents write (a comment, a review's message, a finding), its mentions and item links as chips (the
 * items' linked). `inline`: within a line (a list's row), else its own paragraph keeping its line breaks.
 */
export function RichText({
  space,
  text,
  inline = false,
  class: cls = "",
}: {
  space: SpaceOut;
  text: string;
  inline?: boolean;
  class?: string;
}) {
  const all = parts(text);
  const pick = useItemPicks(
    space,
    all.flatMap((p) => (typeof p !== "string" && p.sigil === "#" ? [p.id] : [])),
  );
  const pieces = all.map((p, i) =>
    typeof p === "string" ? (
      p
    ) : p.sigil === "@" ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
      <span key={i} class={`${CHIP} bg-blue-50 text-blue-800`}>
        {`@${p.label}`}
      </span>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
      <ItemChip key={i} space={space} id={p.id} label={p.label} pick={pick(p.id)} />
    ),
  );
  return inline ? (
    <span class={`break-words ${cls}`}>{pieces}</span>
  ) : (
    // Selectable in the iOS app too (what's read, not the app's own).
    <p class={`break-words whitespace-pre-wrap ${cls || "text-zinc-800"}`} data-selectable="">
      {pieces}
    </p>
  );
}

/** The space's members, then whoever else may be mentioned here. */
function mentionable(members: readonly Member[] | undefined, speakers: readonly Member[]): Member[] {
  const known = new Set((members ?? []).map((m) => m.id));
  return [...(members ?? []), ...speakers.filter((s) => !known.has(s.id))];
}

/**
 * A text box where `@` suggests members, `#` items and `:` emoji (arrows and Enter or Tab pick), writing what's
 * picked as the server stores it (`@[Name](usr_…)`, `#[Title](itm_…)`). Keys it doesn't use go to `onKeyDown`.
 */
export function MentionTextarea(p: {
  space: SpaceOut;
  value: string;
  onValue: (v: string) => void;
  about: WrittenAbout;
  rows?: number;
  placeholder?: string;
  /** Its name, when no `Field` labels it. */
  label?: string;
  autoFocus?: boolean;
  class?: string;
  onKeyDown?: (e: h.JSX.TargetedKeyboardEvent<HTMLTextAreaElement>) => void;
}) {
  const { space, value: text, about } = p;
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  /** Where the `@` or `#` being looked up is; null: no lookup. */
  const [session, setSession] = useState<number | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const members = mentionable(useSpacePeople(space), about.speakers ?? []);
  const open = session === null ? null : lookup(text, caret, session);
  const options = useSuggestions(space, members, open, { item: about.item, people: about.people });
  /** The caret moved (or the text changed): the lookup goes on while the caret stays in it. */
  const moved = (v: string, at: number, typed: boolean) => {
    setCaret(at);
    const still = session !== null && lookup(v, at, session) ? session : null;
    setSession(still ?? (typed ? opened(v, at) : null));
  };
  const pick = (s: Suggestion) => {
    if (!open) {
      return;
    }
    const before = text.slice(0, open.start);
    const after = text.slice(caret);
    if (s.sigil === ":") {
      rememberEmoji(s.emoji.u);
    }
    const shown = `${token(s)} `;
    p.onValue(before + shown + after);
    const to = before.length + shown.length;
    setCaret(to);
    setSession(null);
    setActive(0);
    requestAnimationFrame(() => wrap.current?.querySelector("textarea")?.setSelectionRange(to, to));
  };
  // Nothing matches a name with spaces in it (just words after an @: "@Ana said…"), or an emoji (":" then
  // anything): the list steps aside.
  const shown = open !== null && !(options?.length === 0 && (SPACE.test(open.query) || open.sigil === ":"));
  const n = options?.length ?? 0;
  return (
    <div ref={wrap} class="relative">
      <Textarea
        value={text}
        rows={p.rows ?? 2}
        placeholder={p.placeholder}
        aria-label={p.label}
        autoFocus={p.autoFocus ?? false}
        role="combobox"
        aria-expanded={shown}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={shown && n > 0 ? `${listId}-${active}` : undefined}
        class={p.class ?? "text-sm"}
        onValue={(v, e) => {
          p.onValue(v);
          setActive(0);
          moved(v, e.currentTarget.selectionStart, true);
        }}
        onKeyUp={(e) => {
          if (!["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) {
            moved(text, e.currentTarget.selectionStart, false);
          }
        }}
        onClick={(e) => moved(text, e.currentTarget.selectionStart, false)}
        onBlur={() => setSession(null)}
        onKeyDown={(e) => {
          if (shown) {
            if ((e.key === "ArrowDown" || e.key === "ArrowUp") && n > 0) {
              e.preventDefault();
              setActive((active + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
              return;
            }
            const s = options?.[active];
            if ((e.key === "Enter" || e.key === "Tab") && !e.metaKey && !e.ctrlKey && s) {
              e.preventDefault();
              pick(s);
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setSession(null);
              return;
            }
          }
          p.onKeyDown?.(e);
        }}
      />
      {shown ? (
        <Suggestions
          space={space}
          id={listId}
          options={options}
          active={active}
          onPick={pick}
          onHover={setActive}
          anchor={wrap.current}
        />
      ) : null}
    </div>
  );
}
